import { ReactElement, useEffect, useRef, useState } from 'react';
import '../styles/basic.css';
import Vector2 from '../utility/Vector2';

interface AvoiderProps {
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
    content: ReactElement,
    wrapper?: HTMLElement
}

export function Avoider(props: AvoiderProps) {
    let animationFrame: number;
    const width : number = 64;
    const height : number = 64;
    const slowing : number = 0.95;
    const rotSpeed : number = 3;
    const speed : number = 20;
    const pad : number = 4;
    const [pos, setPos] = useState(new Vector2(props.x - width / 2, props.y - height / 2));
    const posRef = useRef(pos);
    const rotRef = useRef(0);
    const velRef = useRef(new Vector2(0, 0));
    const speedRef = useRef(0);
    const cursorRef = useRef<Vector2>(new Vector2(0, 0));

    const onMouseMove = (e: MouseEvent) => {
        if (props.wrapper) {
            const boundingRect = props.wrapper.getBoundingClientRect();
            cursorRef.current.x = e.clientX - boundingRect.left;
            cursorRef.current.y = e.clientY - boundingRect.top;
        } else {
            cursorRef.current.x = e.clientX;
            cursorRef.current.y = e.clientY;
        }
    };

    const onUpdate = () => {
        let diffX = cursorRef.current.x - (posRef.current.x - window.scrollX);
        let diffY = cursorRef.current.y - (posRef.current.y - window.scrollY);
        
        const dist = Math.sqrt(Math.pow(diffX, 2) + Math.pow(diffY, 2));
        
        if (dist < props.r && dist > 0)
        {
            const distLerp = 1 - dist / props.r;
            velRef.current.x = -diffX / dist;
            velRef.current.y = -diffY / dist;
            speedRef.current = Math.max(speedRef.current, distLerp);
            rotRef.current += distLerp * (diffX > 0 ? rotSpeed : -rotSpeed);
        }


        const newPos = new Vector2(posRef.current.x, posRef.current.y);
        newPos.x += velRef.current.x * speedRef.current * speed;
        newPos.y += velRef.current.y * speedRef.current * speed;
        if (newPos.x >= window.innerWidth - width - pad && velRef.current.x > 0)
        {
            velRef.current.x = -velRef.current.x;
            newPos.x = window.innerWidth - width - pad;
        }
        if (newPos.x <= pad && velRef.current.x < 0)
        {
            velRef.current.x = -velRef.current.x;
            newPos.x = pad;
        }
        if (newPos.y >= window.innerHeight - height - pad && velRef.current.y > 0)
        {
            velRef.current.y = -velRef.current.y;
            newPos.y = window.innerHeight - height - pad;
        }
        if (newPos.y <= pad && velRef.current.y < 0)
        {
            velRef.current.y = -velRef.current.y;
            newPos.y = pad;
        }

        speedRef.current *= slowing;

        posRef.current.x = newPos.x;
        posRef.current.y = newPos.y;
        setPos(newPos);
    };

    const init = () => {
        window.addEventListener("mousemove", onMouseMove);
        loop();
    }

    const loop = () => {
        onUpdate();
        animationFrame = requestAnimationFrame(loop);
    };


    useEffect(() => {
        init();
        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener("mousemove", onMouseMove);
        }
    }, [props]);

    return (
        <div 
        className='ab-div'
        style={{
            top: pos.y,
            left: pos.x,
            width: width,
            height: height,
            transform: 'rotate('+ rotRef.current + 'deg)'
        }}>
            {props.content}
        </div>
    );
}